import { toast } from '@/components/ui/use-toast';
import { identifyFullStoryUser } from '@/lib/utils/fullstory';

import type { AllAuthResponse } from './allauth';
import { getAuth, login, logout } from './allauth';
import { isMfaRequiredError, MFA_REQUIRED_MESSAGE } from './mfaError';

/**
 * Auth interface to match the design of react-router
 * https://github.com/remix-run/react-router/blob/dev/examples/auth-router-provider/src/App.tsx
 *
 * Additional methods for forgot password, etc., should be added
 */
interface AuthProvider {
  isAuthenticated: boolean;
  checkAuth(): Promise<boolean>;
  username: null | string;

  login(data: Record<string, unknown>): Promise<AllAuthResponse | void>;
  logout(): Promise<void>;
}

export const allAuthProvider: AuthProvider = {
  isAuthenticated: false,
  username: null,

  async checkAuth() {
    try {
      const msg = await getAuth();

      if ([401, 410].includes(msg.status)) {
        allAuthProvider.username = null;
        allAuthProvider.isAuthenticated = false;
        return false;
      }

      if (msg.status === 200 && msg.meta.is_authenticated) {
        const user = msg.data.user;
        allAuthProvider.username = user.email;
        allAuthProvider.isAuthenticated = true;
        identifyFullStoryUser(String(user.id), user.email);
        return true;
      }

      // Any other response (e.g. a 403 mfa_required from the session endpoint)
      // means the session is not usable. Mark unauthenticated so the flag and
      // the return value can never disagree — the protectedLoader gates its
      // redirect on this return value.
      allAuthProvider.username = null;
      allAuthProvider.isAuthenticated = false;

      // Surface the MFA-gate denial for UX parity with the data-endpoint path
      // (which shows the same destructive toast via handleMfaRequired). The
      // loader's redirect stops the protected page from rendering, so this
      // toast fires at most once per failed session check.
      if (isMfaRequiredError(msg)) {
        toast({ title: MFA_REQUIRED_MESSAGE, variant: 'destructive' });
      } else {
        console.warn(`Authentication failed unexpectedly`, msg);
      }
      return false;
    } catch {
      allAuthProvider.username = null;
      allAuthProvider.isAuthenticated = false;
      return false;
    }
  },

  async login(data) {
    const msg = await login(data);

    if ([401, 410].includes(msg.status)) {
      allAuthProvider.username = null;
      allAuthProvider.isAuthenticated = false;
    }

    if (msg.status === 200 && msg.meta.is_authenticated) {
      const user = msg.data.user;
      allAuthProvider.username = user.email;
      allAuthProvider.isAuthenticated = true;
      identifyFullStoryUser(String(user.id), user.email);
    }
  },
  async logout() {
    try {
      await logout();
    } finally {
      allAuthProvider.username = null;
      allAuthProvider.isAuthenticated = false;
    }
  },
};
